import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import { Package, CheckCircle, Tag, AlertTriangle, Copy, FileWarning } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, Area, AreaChart
} from 'recharts';
import { operationalDashboardService } from '../../services/operationalDashboard';

interface Props {
  filters: { periodo: string; dataInicio?: string; dataFim?: string };
}

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#06b6d4', '#f97316', '#ec4899'];

const OperationalDashboardOverview: React.FC<Props> = ({ filters }) => {
  const [metrics, setMetrics] = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilters = useMemo(() => filters, [filters.periodo, filters.dataInicio, filters.dataFim]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [metricsRes, chartsRes] = await Promise.all([
          operationalDashboardService.getOverview(stableFilters),
          operationalDashboardService.getCharts(stableFilters)
        ]);
        setMetrics(metricsRes.data);
        setCharts(chartsRes.data);
      } catch (err: unknown) {
        setError('Erro ao carregar dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [stableFilters]);

  if (loading) {
    return <div className="op-dashboard-loading"><div className="op-dashboard-spinner" />Carregando visão geral...</div>;
  }

  if (error) {
    return <div className="op-dashboard-error">{error}</div>;
  }

  if (!metrics) return null;

  return (
    <>
      <h2 className="op-dashboard-section-title">Indicadores Gerais</h2>
      <div className="op-dashboard-cards">
        <div className="op-dashboard-card purple">
          <div className="op-dashboard-card-icon"><Package size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_paletes || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Paletes Conferidos</div>
        </div>
        <div className="op-dashboard-card blue">
          <div className="op-dashboard-card-icon"><CheckCircle size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_cargas || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Cargas Conferidas</div>
        </div>
        <div className="op-dashboard-card green">
          <div className="op-dashboard-card-icon"><Tag size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_etiquetas || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Etiquetas Bipadas</div>
        </div>
        <div className="op-dashboard-card red">
          <div className="op-dashboard-card-icon"><AlertTriangle size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_divergencias || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Divergências</div>
        </div>
        <div className="op-dashboard-card yellow">
          <div className="op-dashboard-card-icon"><Copy size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_duplicadas || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Duplicidades</div>
        </div>
        <div className="op-dashboard-card orange">
          <div className="op-dashboard-card-icon"><FileWarning size={20} /></div>
          <div className="op-dashboard-card-value">{(metrics.total_ocorrencias || 0).toLocaleString('pt-BR')}</div>
          <div className="op-dashboard-card-label">Ocorrências</div>
        </div>
      </div>

      {charts && (
        <>
          {/* Volume por Hora */}
          {(charts.volume_por_hora || []).length > 0 ? (
            <div className="op-dashboard-chart-container">
              <h3 className="op-dashboard-chart-title">Volume de Conferência por Hora</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={(charts.volume_por_hora || []).map((v: any) => ({
                  hora: `${v.hora}h`,
                  total: v.total
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis dataKey="hora" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, color: '#e2e8f0' }} />
                  <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="rgba(59,130,246,0.2)" name="Leituras" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="op-dashboard-empty">Sem dados de volume por hora no período</div>
          )}

          {/* Evolução Diária */}
          {(charts.volume_diario || []).length > 0 ? (
            <div className="op-dashboard-chart-container">
              <h3 className="op-dashboard-chart-title">Evolução Diária</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.volume_diario.map((d: any) => ({
                  dia: new Date(d.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
                  ok: d.ok,
                  divergencias: d.divergencias,
                  duplicadas: d.duplicadas
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
                  <XAxis dataKey="dia" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, color: '#e2e8f0' }} />
                  <Bar dataKey="ok" fill="#22c55e" name="OK" radius={[4, 4, 0, 0]} stackId="a" />
                  <Bar dataKey="divergencias" fill="#ef4444" name="Divergências" radius={[0, 0, 0, 0]} stackId="a" />
                  <Bar dataKey="duplicadas" fill="#eab308" name="Duplicadas" radius={[4, 4, 0, 0]} stackId="a" />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="op-dashboard-empty">Sem dados de evolução diária no período</div>
          )}

          {/* Gráficos por Área de Entrega */}
          <div className="op-dashboard-charts-row">
            <div className="op-dashboard-chart-container">
              <h3 className="op-dashboard-chart-title">Etiquetas por Área de Entrega</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={(charts.etiquetas_por_area || []).slice(0, 10)}
                    dataKey="total"
                    nameKey="area"
                    cx="50%" cy="50%"
                    outerRadius={100}
                    label={({ area, percent }: any) => `${area} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {(charts.etiquetas_por_area || []).slice(0, 10).map((_: any, i: number) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="op-dashboard-chart-container">
              <h3 className="op-dashboard-chart-title">Pedidos por Área de Entrega</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={(charts.pedidos_por_area || []).slice(0, 10)}
                    dataKey="total"
                    nameKey="area"
                    cx="50%" cy="50%"
                    outerRadius={100}
                    label={({ area, percent }: any) => `${area} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {(charts.pedidos_por_area || []).slice(0, 10).map((_: any, i: number) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default OperationalDashboardOverview;
